import React, { useContext, useEffect, useMemo, useState } from "react";
import ProfileDetailsHeader from "./ProfileDetailsHeader";
import BaseWrapper from "../../components/ui/BaseWrapper";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import { brandFontFamily, brandInputTextColor, brandPrimaryColor } from "../../utils/variables";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CustomerProfile from "./CustomerProfile";
import ClientProfile from "./ClientProfile";
import FreelancerProfile from "./FreelancerProfile";
import routes from "../../routes";
import { LayoutContext } from "../../context/LayoutContext";
import { useQuery } from "@tanstack/react-query";
import { fetchUserDetails } from "../../services/api/user";
import useAuth from "../../hooks/useAuth";
import { UserProfileStatus, UserType } from "../../utils/enum";
import BaseButton from "../../components/ui/BaseButton";
import ApproveEngineerModal from "../NewUsers/components/ApproveEngineerModal";
import { fetchQualificationTypes } from "../../services/api/qualification";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const CustomTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    minWidth: 0,
  },
  height: "38px",
  width: "230px",
  fontWeight: 300,
  fontSize: "16px",
  boxShadow: "0px -1px 8px 0px rgba(0, 0, 0, 0.09)",

  borderRadius: "6px",
  // padding: 0,
  margin: "20px 0",
  background: "white",
  marginRight: theme.spacing(1),
  color: brandInputTextColor,
  fontFamily: brandFontFamily,
  "&:hover": {
    /* color: "#40a9ff",
          opacity: 1, */
  },
  "&.Mui-selected": {
    color: brandPrimaryColor,
    fontWeight: 400,
    borderBottom: `1px solid ${brandPrimaryColor}`,
  },
  "&.Mui-focusVisible": {
    //   backgroundColor: "#d1eaff",
  },
}));

// Roles that have been given their own single, tab-less profile page. Every other role
// still gets the tabbed profile below until it is separated the same way.
const ROLE_PAGES = {
  [UserType.CUSTOMER]: CustomerProfile,
  [UserType.CLIENT]: ClientProfile,
  [UserType.FREELANCER]: FreelancerProfile,
};

const ProfileDetails = () => {
  const { auth, authRole } = useAuth();
  const { isBannerVisible } = useContext(LayoutContext);
  const [value, setValue] = useState(0);

  const { id: userId } = useParams();
  const [searchParams] = useSearchParams();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const direct = searchParams.get("direct") === "true";
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isPending: isUserLoading,
    error: isUserError,
    data: userData,
    refetch: refetchMe,
  } = useQuery({
    queryKey: ["userData", userId, direct],
    queryFn: () => fetchUserDetails(userId, direct),
    // Don't retry on client errors (e.g. 403 Forbidden) — the result won't change.
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    enabled: !!userId,
  });
  const viewedUserRole = userData?.role?.role;
  const isSelfView = auth?.user?.id === userData?.id;

  const isForbidden = isUserError?.response?.status === 403;

  // Unrelated freelancers must not see each other. `company` only comes back when the
  // backend recognises the viewer as entitled to it (EngineerResponseDto), so an
  // outside freelancer gets no company here and stays blocked.
  const isOwnFreelanceCompanyOwner =
    !!userData?.isCompanyOwner &&
    !!auth?.user?.company?.id &&
    userData?.company?.id === auth.user.company.id;

  const canAccessProfile = !(
    authRole === UserType.FREELANCER &&
    !isSelfView &&
    viewedUserRole === UserType.FREELANCER &&
    !userData?.isCompanyMember &&
    !isOwnFreelanceCompanyOwner
  );

  // An admin works from the tabbed view, which keeps each record on its own tab.
  // Everyone else gets the role's single page, with no tabs at all.
  // Customers have their own page for every viewer; the admin's customer management
  // lives on the admin customer page.
  const RolePage =
    viewedUserRole === UserType.CUSTOMER || authRole !== UserType.ADMIN
      ? ROLE_PAGES[viewedUserRole]
      : null;

  // Qualification types only feed the tab components, so a role page - which has no
  // tabs - would otherwise fetch this list and discard it.
  const { data: qualificationsTypesData } = useQuery({
    queryKey: ["qualificationsTypes"],
    queryFn: fetchQualificationTypes,
    enabled: !!userData && canAccessProfile && !RolePage,
  });

  const userProfileRoute = routes.find((route) => route.path === "/user/details");

  // Personal Info holds the identity documents, tax details, insurance and home
  // address, so only the person themselves may see it. A freelance company owner can
  // reach a member's profile, but never that member's personal data. Defaults to
  // hidden while userData is still loading, so the PII never flashes into view.
  const hidePersonalInfo = authRole === UserType.FREELANCER && !isSelfView;

  const allTabs = useMemo(() => {
    // Wait for the user's role, so a customer's page never flashes tabs it won't keep.
    if (RolePage || !viewedUserRole) return [];
    return (
      userProfileRoute?.childs?.filter((child) => {
        if (!child.roles || !child.roles.includes(authRole)) return false;
        if (
          child.path.includes("profileInformation") &&
          (hidePersonalInfo || userData?.isCompanyOwner)
        )
          return false;
        // The company's own tabs belong to the vendor owner's page only.
        if (child.companyOwnerOnly && !userData?.isCompanyOwner) return false;
        return true;
      }) || []
    );
  }, [
    RolePage,
    viewedUserRole,
    userProfileRoute,
    authRole,
    hidePersonalInfo,
    userData?.isCompanyOwner,
  ]);

  useEffect(() => {
    const currentTabIndex = allTabs.findIndex((tab) =>
      location.pathname.includes(tab.path.split("/:id")[0]),
    );
    if (currentTabIndex !== -1) {
      setValue(currentTabIndex);
    }
  }, [location.pathname, allTabs]);

  // A link pointing straight at a tab this viewer can't have - a vendor opening a
  // worker on /profileInformation - lands on their first allowed tab instead, so the
  // address bar matches what is actually rendered. `search` carries ?direct=true.
  useEffect(() => {
    if (!userData || !allTabs.length) return;
    const isAllowedTab = allTabs.some((tab) =>
      location.pathname.includes(tab.path.split("/:id")[0]),
    );
    if (!isAllowedTab) {
      navigate(`/user/details/${allTabs[0].path.replace(":id", userId)}${location.search}`, {
        replace: true,
      });
    }
  }, [location.pathname, location.search, userData, allTabs.length, userId, navigate]);

  // An old link aimed at one of a role page's former tabs - an admin opening
  // /bankAccounts or /profileInformation - now resolves to the one page that role
  // has, so the address bar matches what is rendered.
  useEffect(() => {
    if (!RolePage) return;
    if (location.pathname.includes("/user/details/profile/")) return;
    navigate(`/user/details/profile/${userId}${location.search}`, { replace: true });
  }, [RolePage, location.pathname, location.search, userId, navigate]);

  const handleChange = (event, newValue) => {
    const selectedTab = allTabs[newValue];
    navigate(`/user/details/${selectedTab.path.replace(":id", userId)}`); // Replace with dynamic ID if necessary
    setValue(newValue);
  };

  const qualificationTypeMap = useMemo(() => {
    if (!qualificationsTypesData?.data?.data) return {};
    return qualificationsTypesData?.data?.data?.reduce((acc, item) => {
      acc[item.name.toLowerCase()] = item.id;
      return acc;
    }, {});
  }, [qualificationsTypesData]);

  if (isForbidden || (!isUserLoading && userData && !canAccessProfile)) {
    return <h1 className="flex items-center justify-center relative top-[100px]">Unauthorized</h1>;
  }

  // A profile is approved from its own page while it is still waiting on review: before
  // it is sent, after it is sent, or after an admin asked for more. Once approved or
  // switched off, activation is handled from the lists.
  const canApproveProfile =
    authRole === UserType.ADMIN &&
    !!userData?.id &&
    [UserProfileStatus.PENDING, UserProfileStatus.SUBMITTED, UserProfileStatus.UNAPPROVE].includes(
      userData?.status,
    );

  return (
    <div className="bg-light h-full">
      <div
        className={`border-b border-zinc-300 py-6 sticky  bg-white ${
          isBannerVisible ? "top-[95px]" : "top-[40px]"
        }`}
        style={{ zIndex: 9 }}
      >
        <BaseWrapper>
          <ProfileDetailsHeader user={userData} />
        </BaseWrapper>
      </div>
      <BaseWrapper className={`relative ${isBannerVisible ? "top-24" : "top-10"}`}>
        {RolePage ? (
          <div className="pt-6">
            <RolePage user={userData} refetchMe={refetchMe} canAccessProfile={canAccessProfile} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <Tabs
                TabIndicatorProps={{
                  style: {
                    display: "none", // Hide the indicator
                  },
                }}
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                {allTabs?.map((tab, index) => {
                  if (!tab?.roles?.includes(authRole)) return null;
                  return (
                    <CustomTab
                      key={index}
                      label={tab.text}
                      {...a11yProps(index)}
                      sx={{
                        minHeight: "38px",
                        marginRight: "40px",
                      }}
                    />
                  );
                })}
              </Tabs>

              {canApproveProfile && (
                <BaseButton
                  variant="contained"
                  color="primary"
                  className="py-[5px] whitespace-nowrap"
                  onClick={() => setIsApproveModalOpen(true)}
                >
                  Approve Profile
                </BaseButton>
              )}
            </div>

            {allTabs.map((tab, index) => {
              if (!tab?.roles?.includes(authRole)) return null;
              return (
                <CustomTabPanel value={value} index={index} key={index}>
                  {React.cloneElement(tab.component, {
                    user: userData,
                    refetchMe,
                    qualificationTypeMap: qualificationTypeMap,
                    canAccessProfile,
                  })}
                </CustomTabPanel>
              );
            })}
          </>
        )}
      </BaseWrapper>

      {isApproveModalOpen && (
        <ApproveEngineerModal
          isApproveEngineerModalOpen={isApproveModalOpen}
          setIsApproveEngineerModalOpen={setIsApproveModalOpen}
          selectedEngineer={userData}
          onStatusChange={() => refetchMe()}
        />
      )}
    </div>
  );
};

export default ProfileDetails;
